import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Card } from './Card';

export interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Brand icon-box glyph (lucide element) — the "icon-card" look. */
  icon?: ReactNode;
  /** The state line ("Open day", "Inbox is empty"). */
  title: ReactNode;
  /** The offer-a-move line — voice rule: acknowledge state, then offer a move. */
  move?: ReactNode;
  /** Optional trailing node (CTA button, hint chevron). */
  action?: ReactNode;
  /** dashed (default) for in-flow empties; solid for the timeline overlay card. */
  dashed?: boolean;
  /** quiet = tertiary text (secondary lists); primary = t1 state line. */
  tone?: 'primary' | 'quiet';
  align?: 'center' | 'start';
}

/**
 * The house empty state — eight ad-hoc versions of this card existed before
 * extraction. Encodes the voice rule mechanically: a state line, then a move.
 */
export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  {
    icon,
    title,
    move,
    action,
    dashed = true,
    tone = 'primary',
    align = 'center',
    className,
    ...rest
  },
  ref,
) {
  return (
    <Card
      ref={ref}
      variant={dashed ? 'dashed' : 'solid'}
      className={cn(align === 'center' ? 'text-center' : 'text-start', className)}
      {...rest}
    >
      {icon ? (
        <div
          aria-hidden="true"
          className={cn(
            'mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-brand-line bg-brand-soft text-brand-ink',
            align === 'center' && 'mx-auto',
          )}
        >
          {icon}
        </div>
      ) : null}
      <p
        className={cn(
          icon
            ? 'text-title font-semibold tracking-tight text-t1'
            : tone === 'quiet'
              ? 'text-body text-t3'
              : 'text-body font-medium text-t1',
        )}
      >
        {title}
      </p>
      {move ? (
        <p
          className={cn(
            'mt-1 leading-snug',
            icon
              ? 'text-body-lg text-t2'
              : tone === 'quiet'
                ? 'text-caption text-t3'
                : 'text-caption text-t2',
          )}
        >
          {move}
        </p>
      ) : null}
      {action ? (
        <div className={cn('mt-3', align === 'center' && 'flex justify-center')}>{action}</div>
      ) : null}
    </Card>
  );
});
